<template>
  <div data-page-name="on-premises-object-storage">
    <section class="hero">
      <div class="wrapper">
        <div class="left">
          <h1 v-html="$t('on-premises-object-storage.hero.title')" />
          <p v-html="$t('on-premises-object-storage.hero.description-1')" />

          <div class="buttons">
            <Buttons.Link :to="REQUEST_DEMO_URL">
              {{ $t(PATHS.REQUEST_DEMO.i18nKey) }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Link>

            <Buttons.Regular
              fill="outline"
              accent="orange"
              @click="setFormState('visible', true)"
            >
              {{ $t('Download_White_Paper') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Regular>
          </div>
        </div>

        <div class="right">
          <NuxtImg
            src="/redesign/images/server/server-without-ootbi-logo-front.webp"
            alt="Object first server top and front view"
            width="496"
            height="127"
            sizes="xs:328px md:704px xl:496px"
            densities="x1 x2"
            format="webp"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section class="purple-short">
      <div class="wrapper">
        <div class="content">
          <h2 v-html="$t('on-premises-object-storage.purple-1.title')" />
          <p v-html="$t('on-premises-object-storage.purple-1.p-1')" />
          <p v-html="$t('on-premises-object-storage.purple-1.p-2')" />
        </div>
      </div>
    </section>

    <section class="tabs">
      <div class="wrapper">
        <TabList v-slot="{ tab, index, active }" :tabs="TABS" :active-tab-index>
          <button
            v-html="tab"
            :class="{ active }"
            @click="setActiveTabIndex(index)"
          />
        </TabList>

        <ul class="contents">
          <template v-for="(content, index) of TABS_CONTENT" :key="index">
            <li
              v-if="activeTabIndex === index"
              v-html="content"
              class="tab-content"
            />
          </template>
        </ul>
      </div>
    </section>

    <section class="benefits">
      <div class="wrapper">
        <h2 v-html="$t('on-premises-object-storage.benefits.title')" />

        <ul class="cards">
          <li
            v-for="({ title, icon, description }, index) of CARDS"
            :key="index"
            class="card"
          >
            <strong v-html="title" />
            <component :is="icon" />
            <p v-html="description" />
          </li>
        </ul>
      </div>
    </section>

    <section class="purple-2">
      <div class="wrapper">
        <div class="content">
          <div class="left">
            <h2 v-html="$t('on-premises-object-storage.purple-2.title')" />
            <strong v-html="$t('on-premises-object-storage.purple-2.p-1')" />
            <span v-html="$t('on-premises-object-storage.purple-2.p-2')" />
          </div>

          <div class="right">
            <Buttons.Link :to="REQUEST_DEMO_URL" class="nowrap">
              {{ $t('Request_a_Demo') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Link>
          </div>
        </div>
      </div>
    </section>

    <section class="use-cases">
      <div class="wrapper">
        <h2>{{ $t('on-premises-object-storage.use-cases.title') }}</h2>
        <p>{{ $t('on-premises-object-storage.use-cases.description') }}</p>

        <NuxtImg
          src="/redesign/images/server/server-top-and-front.webp"
          alt="Object first server top and front view"
          width="592"
          height="394"
          sizes="xs:328px md:704px xl:592px"
          densities="x1 x2"
          format="webp"
          loading="lazy"
        />

        <ul>
          <li
            v-for="({ title, description }, index) of USE_CASES"
            :key="`use-cases-${index}`"
          >
            <strong>{{ title }}</strong>
            <p v-html="description" />
          </li>
        </ul>
      </div>
    </section>

    <section class="features">
      <div class="wrapper">
        <div class="intro">
          <h2 v-html="$t('on-premises-object-storage.features.title')" />
          <p
            v-html="$t('on-premises-object-storage.features.description-1')"
            class="description"
          />
        </div>

        <ul class="list-full">
          <li
            v-for="({ title, description }, index) of FEATURES"
            :key="`features-${index}`"
            class="item"
          >
            <strong>{{ title }}</strong>
            <p v-html="description" />
          </li>
        </ul>
      </div>
    </section>

    <section class="key-differences">
      <div class="wrapper">
        <h2 v-html="$t('on-premises-object-storage.differences.title')" />
        <p v-html="$t('on-premises-object-storage.differences.description')" />

        <p class="header">
          {{ $t('on-premises-object-storage.differences.table-header') }}
        </p>

        <table>
          <thead>
            <tr>
              <th>{{ DIFFERENCES[0][0] }}</th>
              <th>{{ DIFFERENCES[1][0] }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in DIFFERENCES[0].slice(1)" :key="index">
              <td v-html="item" />
              <td v-html="DIFFERENCES[1][index + 1]" />
            </tr>
          </tbody>
        </table>

        <div class="list">
          <div class="columns">
            <ul v-for="column of DIFFERENCES">
              <li v-for="row of column">
                <p v-html="row" />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="best-storage">
      <div class="wrapper">
        <div class="left">
          <h2 v-html="$t('on-premises-object-storage.best-storage.title')" />
          <p
            v-html="$t('on-premises-object-storage.best-storage.description')"
          />

          <Buttons.Link :to="REQUEST_DEMO_URL" fill="solid" accent="orange">
            {{ $t('Request_a_Demo') }}
            <SvgIcon name="arrow-forward" />
          </Buttons.Link>
        </div>

        <div class="right">
          <NuxtImg
            src="/redesign/images/server/server-perspective-without-front-panel.webp"
            alt="perspective view of the server with the front panel removed"
            sizes="xs:328px md:592px"
            densities="x1 x2"
            format="webp"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section class="related-topics">
      <div class="wrapper">
        <h2>{{ $t('Related_topics') }}</h2>

        <ul class="list">
          <li
            v-for="{ slug, title } of RELATED_TOPICS_LINKS"
            :key="slug"
            class="item"
          >
            <ButtonLinkUnderlined :to="createLink(slug)" target="_blank">
              {{ title }}
            </ButtonLinkUnderlined>
          </li>
        </ul>
      </div>
    </section>

    <section class="additional-info">
      <h2>{{ $t('on-premises-object-storage.additional-info.title') }}</h2>
      <p v-html="$t('on-premises-object-storage.additional-info.content')" />
    </section>
  </div>

  <ClientOnly>
    <Teleport to="#overlay">
      <Transition name="scale-fade">
        <Forms.Wrapper
          v-if="formState.visible"
          can-close
          @close="setFormState('visible', false)"
        >
          <template #title>
            <strong>
              {{
                formState.submitted
                  ? $t('Download_Asset')
                  : $t('Provide_your_details_to_get_the_asset')
              }}
            </strong>
          </template>

          <transition name="fade" mode="out-in">
            <Modals.DownloadAsset v-if="formState.submitted" />

            <Forms.GetAsset
              v-else
              @loading="setLoading"
              @submitted="setFormState('submitted', true)"
            />
          </transition>
        </Forms.Wrapper>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<script lang="ts" setup>
// components
import Buttons from '~/components/redesign/Buttons';
import ButtonLinkUnderlined from '~/components/redesign/ButtonLinkUnderlined.vue';
import TabList from '~/components/redesign/TabList.vue';
import Modals from '~/components/redesign/Modals';
import Forms from '~/components/redesign/Forms';

// stores
import useUIStore from '~/stores/ui';

// composable
import { useI18n } from '#i18n';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// constants
import { PATHS } from '~/constants/nav-items';

// utils
import buildUTM from '~/utils/buildUTM';

// icons
import SvgLockColorfull from '~/assets/svg/lock-colorfull.svg?component';
import SvgLikeColorfull from '~/assets/svg/like-colorfull.svg?component';
import SvgRocketColorfull from '~/assets/svg/rocket-colorfull.svg?component';
import SvgScalabilityColorfull from '~/assets/svg/scalability-colorfull.svg?component';
import SvgUpToDateColorfull from '~/assets/svg/up-to-date-colorfull.svg?component';
import SvgHeartColorfull from '~/assets/svg/heart-colorfull.svg?component';

defineOptions({
  components: {
    SvgLockColorfull,
    SvgLikeColorfull,
    SvgRocketColorfull,
    SvgScalabilityColorfull,
    SvgUpToDateColorfull,
    SvgHeartColorfull,
  },
});

useApiSeo('on-premises-object-storage');

const { t } = useI18n();
const localePathWithSlash = useLocalePathWithSlash();

const REQUEST_DEMO_URL =
  localePathWithSlash(PATHS.REQUEST_DEMO.path) +
  buildUTM({
    source: 'guide',
    campaign: 'custom-guide',
    content: 'on-premises-object-storage',
  });

const { setOverlay, setLoading } = useUIStore();

const formState = reactive({ visible: false, submitted: false });
const setFormState = <K extends keyof typeof formState>(
  key: K,
  value: (typeof formState)[K],
) => {
  formState[key] = value;
};

watchEffect(() => {
  setOverlay({ visible: formState.visible });
});

const activeTabIndex = ref(0);
const setActiveTabIndex = (index: number) => {
  activeTabIndex.value = index;
};

const TABS = [
  t('on-premises-object-storage.tabs-1.title'),
  t('on-premises-object-storage.tabs-2.title'),
  t('on-premises-object-storage.tabs-3.title'),
];

const TABS_CONTENT = [
  t('on-premises-object-storage.tabs-1.content'),
  t('on-premises-object-storage.tabs-2.content'),
  t('on-premises-object-storage.tabs-3.content'),
];

const CARDS = [
  {
    title: t('on-premises-object-storage.benefits.cards-1.title'),
    icon: 'SvgLockColorfull',
    description: t('on-premises-object-storage.benefits.cards-1.description'),
  },
  {
    title: t('on-premises-object-storage.benefits.cards-2.title'),
    icon: 'SvgLikeColorfull',
    description: t('on-premises-object-storage.benefits.cards-2.description'),
  },
  {
    title: t('on-premises-object-storage.benefits.cards-3.title'),
    icon: 'SvgRocketColorfull',
    description: t('on-premises-object-storage.benefits.cards-3.description'),
  },
  {
    title: t('on-premises-object-storage.benefits.cards-4.title'),
    icon: 'SvgScalabilityColorfull',
    description: t('on-premises-object-storage.benefits.cards-4.description'),
  },
  {
    title: t('on-premises-object-storage.benefits.cards-5.title'),
    icon: 'SvgUpToDateColorfull',
    description: t('on-premises-object-storage.benefits.cards-5.description'),
  },
  {
    title: t('on-premises-object-storage.benefits.cards-6.title'),
    icon: 'SvgHeartColorfull',
    description: t('on-premises-object-storage.benefits.cards-6.description'),
  },
];

const FEATURES = [
  {
    title: t('on-premises-object-storage.features.item-1.title'),
    description: t('on-premises-object-storage.features.item-1.content'),
  },
  {
    title: t('on-premises-object-storage.features.item-2.title'),
    description: t('on-premises-object-storage.features.item-2.content'),
  },
  {
    title: t('on-premises-object-storage.features.item-3.title'),
    description: t('on-premises-object-storage.features.item-3.content'),
  },
  {
    title: t('on-premises-object-storage.features.item-4.title'),
    description: t('on-premises-object-storage.features.item-4.content'),
  },
  {
    title: t('on-premises-object-storage.features.item-5.title'),
    description: t('on-premises-object-storage.features.item-5.content'),
  },
];

const USE_CASES = [
  {
    title: t('on-premises-object-storage.use-cases.item-1.title'),
    description: t('on-premises-object-storage.use-cases.item-1.description'),
  },
  {
    title: t('on-premises-object-storage.use-cases.item-2.title'),
    description: t('on-premises-object-storage.use-cases.item-2.description'),
  },
  {
    title: t('on-premises-object-storage.use-cases.item-3.title'),
    description: t('on-premises-object-storage.use-cases.item-3.description'),
  },
];

const DIFFERENCES = [
  [
    t('on-premises-object-storage.differences.col-1'),
    t('on-premises-object-storage.differences.col-1.item-1'),
    t('on-premises-object-storage.differences.col-1.item-2'),
    t('on-premises-object-storage.differences.col-1.item-3'),
    t('on-premises-object-storage.differences.col-1.item-4'),
    t('on-premises-object-storage.differences.col-1.item-5'),
  ],
  [
    t('on-premises-object-storage.differences.col-2'),
    t('on-premises-object-storage.differences.col-2.item-1'),
    t('on-premises-object-storage.differences.col-2.item-2'),
    t('on-premises-object-storage.differences.col-2.item-3'),
    t('on-premises-object-storage.differences.col-2.item-4'),
    t('on-premises-object-storage.differences.col-2.item-5'),
  ],
];

const createLink = (slug: string) => {
  return localePathWithSlash(PATHS.GUIDES.path + slug);
};

const RELATED_TOPICS_LINKS = [
  {
    title: 'What is Object Storage?',
    slug: 'data-storage/what-is-object-storage/',
  },
  {
    title: 'Secure Data Storage',
    slug: 'data-storage/secure-data-storage/',
  },
  {
    title: 'Immutable Storage',
    slug: 'immutability/immutable-storage/',
  },
  {
    title: 'The 3-2-1 Backup Rule',
    slug: 'data-backup/3-2-1-backup-rule-and-strategy/',
  },
  {
    title: 'The Importance of Data Security',
    slug: 'data-security/data-security-explained-in-details/',
  },
];
</script>

<style lang="scss" scoped>
@import '$/mixins/components';
@import '$/mixins/size';
@import '$/mixins/typography';
@import '$/mixins/flex';
@import '$/variables/shadows';
@import '$/variables/fonts';

@mixin link {
  color: token('link');
  text-decoration-line: underline;

  &:hover {
    background-color: rgba(token('link'), 16%);
  }
}

@mixin card {
  padding: 2rem;
  border-top: 1px solid #d2d2d6;

  @include tablet {
    padding: 1.2rem 1.5rem;
  }

  @include mobile {
    padding: 1.5rem 1rem 1.2rem 1rem;
  }

  strong {
    display: block;
    @include subtitle-1;
    margin-bottom: 2rem;

    @include tablet {
      margin-bottom: 1.5rem;
    }
  }

  p {
    @include body-3;

    @include mobile {
      font-size: 1rem;
      line-height: 1.5rem;
    }

    :deep(a) {
      @include link;
    }
  }
}

@mixin veeam {
  display: inline-block;
  background-color: #00b336;
  color: token('text-reversed');
}

[data-page-name='on-premises-object-storage'] {
  padding-top: 5rem;

  @include tablet {
    padding-top: 3rem;
  }

  @include mobile {
    padding-top: 2.4rem;
  }

  section {
    margin-bottom: 8rem;
  }

  section.hero {
    margin-bottom: 4rem;

    @include mobile {
      margin-bottom: 1rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);
      @include flex-center-sb;

      @include tablet {
        flex-direction: column;
      }

      @include mobile {
        padding: 1rem;
      }

      .left,
      .right {
        width: 50%;

        @include tablet {
          width: 100%;
        }
      }

      .left {
        @include flex-start-start;
        flex-direction: column;

        @include tablet {
          margin-bottom: 3rem;
        }

        h1 {
          @include h2;
          margin-bottom: 2rem;
          @include fluid-max-width(35rem);

          @include tablet {
            margin-bottom: 1.5rem;
            @include fluid-max-width(100%);
          }

          @include mobile {
            margin-bottom: 1rem;
          }

          &:deep(br) {
            display: none;

            @include mobile {
              display: block;
            }
          }
        }

        :deep(p) {
          @include body-3;
          margin-bottom: 0.5rem;
          max-width: 85%;

          @include tablet {
            max-width: 100%;
            margin-bottom: 1.5rem;
          }

          @include mobile {
            margin-bottom: 0.5rem;
          }

          a {
            @include link;
          }
        }

        .buttons {
          @include flex-center-sb;
          gap: 0 2rem;

          margin-top: 2rem;

          @include tablet {
            margin-top: 1rem;
          }

          @include mobile {
            flex-direction: column;
            width: 100%;
            gap: 1.5rem;
          }

          [data-component-name='Buttons.Link'],
          [data-component-name='Buttons.Regular'] {
            @include mobile {
              width: 100%;
            }
          }

          [data-component-name='Buttons.Regular'] {
            svg {
              fill: token('icon-primary');
            }
          }
        }
      }

      .right {
        @include flex-start-center;

        @include tablet {
          width: 100%;
        }

        @include mobile {
          padding: 0 2rem 2rem;
        }

        img {
          @include mobile {
            width: 100%;
            height: auto;
          }
        }
      }
    }
  }

  section.purple-short {
    .wrapper {
      padding: 0rem 2rem;
      @include wrapper(80rem);

      @include tablet {
        padding: 0 2rem;
      }

      @include mobile {
        padding: 0;
        margin: 0;
        width: 100%;
      }

      .content {
        padding: 2rem;
        background-color: token('surf-cont-secon-action');

        @include tablet {
          padding: 1.5rem;
        }

        @include mobile {
          padding: 1.5rem 1rem;
        }

        h2 {
          @include h4;
          color: token('text-reversed');
          padding-bottom: 1rem;
        }

        &:deep(p) {
          color: token('text-reversed');
          @include body-3;
          margin-bottom: 0.5rem;

          a {
            color: token('link-reversed');
            text-decoration: underline;

            &:hover {
              background-color: rgba(token('link-reversed'), 16%);
            }
          }

          strong {
            font-weight: 400;
          }
        }
      }
    }
  }

  section.tabs {
    @include mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      .intro {
        margin-bottom: 5rem;

        @include tablet {
          margin-bottom: 2.4rem;
        }

        @include mobile {
          margin-bottom: 1.5rem;
        }

        h2 {
          @include h3;
          margin-bottom: 2rem;

          @include mobile {
            margin-bottom: 1.5rem;
          }
        }

        :deep(p) {
          @include body-3;

          &:not(:last-of-type) {
            margin-bottom: 0.5rem;
          }

          a {
            @include link;
          }
        }
      }

      [data-component-name='TabList'] {
        button {
          height: 100%;
          width: 100%;
          padding: 1rem 0;

          @include subtitle-3;

          color: token('text-primary');
          transition: color 0.15s ease;

          &.active {
            color: token('text-action');
          }
        }
      }

      ul.contents {
        &:deep(li.tab-content) {
          margin: 5.19rem auto 0 auto;
          @include fluid-max-width(50rem);

          @include tablet {
            margin: 3rem auto 5rem auto;
          }

          @include mobile {
            margin: 1.5rem auto 2.5rem auto;
          }

          p {
            @include body-3;
            margin-bottom: 0.5rem;

            a {
              @include link;
            }

            strong {
              font-weight: 700;
            }
          }

          ul {
            margin-left: 2.4rem;

            :deep(li) {
              position: relative;

              &:before {
                content: '';
                display: block;

                @include fixed-size(7px);
                background-color: token('icon-primary');
                border-radius: 100px;

                position: absolute;
                left: -1.5rem;
                top: 0.9rem;
              }

              strong {
                font-weight: 700;
              }

              p {
                display: inline;
              }
            }
          }
        }
      }
    }
  }

  section.benefits {
    @mixin card {
      @include flex-start-start;
      flex-direction: column;

      padding: 2rem;
      box-shadow: map-get($shadows, 'Elevation 1');

      @include tablet {
        padding: 1.5rem;
      }

      @include mobile {
        padding: 1.5rem 1rem;
      }

      strong {
        @include subtitle-1;
      }

      svg {
        @include fixed-size(3.125rem);
        margin: 1.5rem 0 1.5rem 0;
      }

      &:deep(p) {
        @include body-2;

        a {
          color: token('link');
          text-decoration: underline;

          &:hover {
            background-color: rgba(token('link'), 16%);
          }
        }
      }
    }

    @include mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      h2 {
        @include h3;
        margin-bottom: 5rem;

        @include tablet {
          margin-bottom: 5rem;
          max-width: 100%;
        }

        @include mobile {
          margin-bottom: 1.5rem;
        }
      }

      &::v-deep(p) {
        @include body-3;
        @include fluid-max-width(57rem);

        @include mobile {
          margin-bottom: 1rem;
        }

        a {
          color: token('link');
          text-decoration: underline;

          &:hover {
            background-color: rgba(token('link'), 16%);
          }
        }
      }

      h4 {
        @include h4;
        margin: 5rem 0 2rem;

        @include tablet {
          margin-bottom: 2.5rem;
        }

        @include tablet {
          margin-bottom: 0;
        }
      }

      ul.cards {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: auto;
        grid-gap: 2rem;

        margin-top: 2rem;

        @include tablet {
          grid-template-columns: repeat(2, 1fr);
        }

        @include mobile {
          grid-template-columns: 1fr;
        }

        li.card {
          @include card;
        }
      }
    }
  }

  section.purple-2 {
    @include mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include tablet {
        padding: 0;
      }

      .content {
        padding: 2rem;
        color: token('text-reversed');
        background-color: token('surf-cont-secon-action');

        @include flex-center-sb;
        gap: 0 2rem;

        @include tablet {
          gap: 0 3rem;
          padding: 1.5rem;
        }

        @include mobile {
          gap: 1rem 0;
          flex-direction: column;
          padding: 1.5rem 1rem;
        }

        .left {
          @include flex-stretch-start;
          flex-direction: column;
          gap: 1rem 0;

          @include mobile {
            text-align: center;
          }

          h2 {
            @include h4;

            &:deep(.veeam) {
              @include veeam;
              line-height: 2.5rem;
              padding-left: 0.25rem;

              @include tablet {
                line-height: 2rem;
              }

              @include mobile {
                line-height: 1.4rem;
              }
            }
          }

          strong {
            @include subtitle-1;
          }

          span {
            @include body-3;
          }
        }

        .right {
          @include mobile {
            width: 100%;
          }
        }
      }
    }
  }

  section.use-cases {
    @include mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-gap: 0 1.5rem;

      @include tablet {
        grid-template-columns: 1fr;
        grid-gap: 0 1.5rem;
      }

      @include mobile {
        grid-template-columns: 1fr;
        padding: 0 1rem;
      }

      h2 {
        @include h3;
        margin-bottom: 2rem;

        grid-column: 1/1;
        grid-row: 1/1;

        @include mobile {
          margin-bottom: 1rem;
        }
      }

      p {
        @include body-3;

        grid-column: 1/1;
        grid-row: 2/2;
      }

      img {
        grid-column: 1/1;
        grid-row: 4/6;

        @include tablet {
          grid-column: 1/1;
          grid-row: 3/3;

          width: 100%;
          height: auto;
        }

        @include mobile {
          margin: 2rem 0;
        }
      }

      ul {
        grid-column: 2/2;
        grid-row: 1/6;

        @include tablet {
          grid-column: 1/1;
          grid-row: 4/4;
        }

        li {
          @include card;

          @include tablet {
            &:last-of-type {
              border-bottom: 1px solid #d2d2d6;
            }
          }
        }
      }
    }
  }

  section.features {
    background-color: token('surf-cont-secon-action');
    color: token('text-reversed');

    padding: 8rem 2rem;

    @include tablet {
      padding: 5rem 2rem;
    }

    @include mobile {
      padding: 5rem 0rem;
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      .intro {
        margin-bottom: 5rem;

        @include tablet {
          margin-bottom: 2.4rem;
        }

        @include mobile {
          margin-bottom: 2rem;
        }

        h2 {
          @include h3;
          margin-bottom: 2rem;

          @include mobile {
            margin-bottom: 1.5rem;
          }
        }

        p {
          @include body-3;

          &:not(:last-of-type) {
            margin-bottom: 0.5rem;
          }
        }
      }

      ul.list-full {
        display: grid;
        grid-template-columns: 1fr;
        grid-gap: 0rem 1.5rem;

        li.item {
          @include card;

          :deep(p) {
            a {
              color: token('link-reversed');
              text-decoration-line: underline;

              &:hover {
                background-color: rgba(token('link'), 16%);
              }
            }
          }

          &:last-of-type {
            border-bottom: 1px solid #d2d2d6;
          }
        }
      }
    }
  }

  section.key-differences {
    @include mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      @include wrapper(80rem);
      padding: 0 2rem;

      @include mobile {
        padding: 0 1rem;
      }

      h2 {
        @include h3;
        max-width: 35ch;

        &:deep(br) {
          @include mobile {
            display: none;
          }
        }
      }

      p {
        @include body-3;
        padding: 2rem 0;

        @include mobile {
          padding: 1rem 0;
        }

        :deep(a) {
          @include link;
        }
      }

      .header {
        @include h4;
        text-align: center;

        border-top: 1px solid token('outline-secondary');
        border-right: 1px solid token('outline-secondary');
        border-left: 1px solid token('outline-secondary');

        margin-top: 2rem;

        @include mobile {
          margin-top: 1rem;
        }
      }

      table {
        @include mobile {
          display: none;
        }

        border-collapse: collapse;

        th,
        td {
          padding: 2rem;
          width: 50%;
          border: 1px solid token('outline-secondary');
        }

        thead {
          tr {
            th {
              @include subtitle-1;
              text-align: center;

              &:first-of-type {
                color: token('text-reversed');
                background-color: token('surf-cont-prim-action');
              }
            }
          }
        }

        tbody {
          tr {
            td {
              @include body-3;
              text-align: start;
              vertical-align: top;

              strong {
                @include subtitle-3;
              }

              &:first-of-type {
                background-color: token('chip-primary-surf-cont');
              }
            }
          }
        }
      }

      .list {
        display: none;

        @include mobile {
          display: block;
        }

        .columns {
          @include flex-start-sb;
          flex-direction: column;

          ul {
            width: 100%;

            li {
              border-top: 1px solid token('outline-secondary');
              border-right: 1px solid token('outline-secondary');
              border-left: 1px solid token('outline-secondary');

              &:first-of-type {
                p {
                  text-align: center;
                  padding: 1rem 0;

                  @include subtitle-1;
                }
              }

              &:last-of-type {
                border-bottom: 1px solid token('outline-secondary');
              }
            }

            &:first-of-type {
              li {
                background-color: token('chip-primary-surf-cont');

                &:first-of-type {
                  color: token('text-reversed');
                  background-color: token('surf-cont-prim-action');

                  border-right: 1px solid token('outline-secondary');
                  border-left: 1px solid token('outline-secondary');
                }
              }
            }

            li {
              border-top: 1px solid token('outline-secondary');
              border-left: 1px solid token('outline-secondary');

              p {
                text-align: start;
                padding: 1rem;

                strong {
                  @include subtitle-3;
                }

                @include body-3;
              }
            }
          }
        }
      }
    }
  }

  section.best-storage {
    @include tablet {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);
      @include flex-center-sb;

      @include tablet {
        gap: 2rem 0;
        flex-direction: column;
      }

      @include mobile {
        padding: 0 1rem;
      }

      .left {
        @include fixed-width(41rem);

        @include flex-start-start;
        flex-direction: column;
        gap: 2.5rem 0;

        @include tablet {
          @include fixed-width(100%);
          gap: 1.5rem 0;
        }

        h2 {
          @include h3;

          &:deep(.veeam) {
            position: relative;
            z-index: -1;

            @include veeam;
          }
        }

        p {
          @include body-3;
        }

        [data-component-name='Buttons.Link'] {
          display: block;
          width: max-content;

          @include mobile {
            width: 100%;
          }
        }
      }

      .right {
        width: 100%;

        img {
          display: block;
          margin: 0 auto;

          @include mobile {
            width: 100%;
            max-width: 328px;
            height: auto;
          }
        }
      }
    }
  }

  section.related-topics {
    @include mobile {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      h2 {
        @include h3;
        margin-bottom: 4rem;

        @include tablet {
          margin-bottom: 2rem;
        }

        @include mobile {
          margin-bottom: 1rem;
        }
      }

      ul.list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(3, auto);
        grid-auto-flow: column;
        grid-gap: 0 2rem;

        @include tablet {
          grid-template-columns: repeat(1, 1fr);
          grid-auto-flow: row;
        }

        li.item {
          margin-top: 2rem;

          @include tablet {
            margin-top: 1.5rem;
          }

          @include mobile {
            margin-top: 1rem;
          }

          &:nth-child(1),
          &:nth-child(4) {
            margin-top: 0;

            @include tablet {
              margin-top: 1.5rem;
            }
          }
        }
      }
    }
  }

  section.additional-info {
    @include wrapper(80rem);
    margin-bottom: 8rem;
    padding: 0 2rem;

    @include mobile {
      margin-bottom: 5rem;
      padding: 0 1rem;
    }

    h2 {
      @include subtitle-1;
      margin-bottom: 1.5rem;
    }

    p {
      @include subtitle-italic-2;

      :deep(a) {
        @include link;
        word-break: break-all;
        font-style: normal;
      }
    }
  }
}
</style>
